import { useQuery } from '@tanstack/react-query';
import api from '@/api/client';
import { API_BASE_URL } from '@/config';
import type { ReportTemplate } from '@/components/reports/ReportCard';

/**
 * React Query hook for fetching report templates
 */
export const useReportTemplates = () => {
  return useQuery<ReportTemplate[]>({
    queryKey: ['reportTemplates'],
    queryFn: async () => {
      try {
        console.log('🔍 Fetching templates from:', `${API_BASE_URL}/v1/reports/templates`);
        const response = await api.get(`${API_BASE_URL}/v1/reports/templates`);
        console.log('📦 Raw API response:', response.data);
        const backendTemplates = response.data || [];
        console.log('📋 Backend templates count:', backendTemplates.length);
        
        if (!Array.isArray(backendTemplates)) {
          console.error('❌ API response is not an array:', backendTemplates);
          return [];
        }
        
        // Map backend response to frontend ReportTemplate interface
        const mappedTemplates: ReportTemplate[] = backendTemplates.map((template: any) => ({
          id: template.id,
          name: template.name,
          description: template.description || '',
          persona: template.persona || [],
          category: template.category || 'general',
          runtimeProfile: (template.runtimeProfile || 'Medium') as 'Short' | 'Medium' | 'Heavy',
          dataFootprint: template.dataFootprint || [],
          supported_context: template.supported_context ? {
            scenario: template.supported_context.scenario || false,
            date_window: template.supported_context.date_window || false,
          } : undefined,
          needs_baseline: template.needs_baseline === true,
        }));
        
        console.log('✅ Mapped templates:', mappedTemplates.length);
        
        // Deduplicate templates by ID (keep first occurrence)
        const uniqueTemplates = Array.from(
          new Map(mappedTemplates.map(t => [t.id, t])).values()
        );
        
        console.log('✅ Unique templates:', uniqueTemplates.length);
        return uniqueTemplates;
      } catch (error: any) {
        console.error('❌ Error fetching templates:', error);
        console.error('❌ Error details:', error.response?.data || error.message);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 2, // Retry failed requests 2 times
  });
};
